import Page from "@/containers/Page";
import { Col, Div, Row } from "@/styles/layout";
import { useEffect, useMemo, useState } from "react";
import { useBranding } from "@/hooks/useBranding";
import { updateBranding } from "@/services/branding.service";
import { Branding } from "@/types/models/Branding";
import Input from "@/components/Input";
import FontSelector from "@/components/FontSelector";
import { getDifferentKeys } from "@/lib/utils";
import { usePageInfo } from "@/hooks/usePageInfo";
import BrandingControl from "@/containers/BrandingControl";
import Card from "@/containers/Card";
import ColorPicker from "@/components/ColorPicker";
import { toast } from "react-hot-toast";
import { useNotifications } from "@/hooks/NotificationsProvider";
import { FiExternalLink } from "react-icons/fi";
import { getWidgetUrl } from "./settings";
import MyButton from "@/components/Button";
import { useAccount } from "@/hooks/useAccount";
import UploadMedia from "@/components/Upload/UploadMedia";

export default function Form() {
  const { account } = useAccount();
  const { branding, refresh, isLoading } = useBranding();
  const { addNotification, removeNotification } = useNotifications();
  const [formBranding, setFormBranding] = useState<
    Partial<Branding> | undefined
  >();

  const [showPreview, setShowPreview] = useState<boolean>(false);

  const handleSave = async () => {
    try {
      if (formBranding) {
        await updateBranding(formBranding);
        refresh();
        toast.success(
          "Nice work! Your online form will update in a few minutes."
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = () => {
    setFormBranding(branding);
  };

  useEffect(() => {
    setFormBranding(branding);
  }, [branding]);

  const saveAvailable = useMemo(() => {
    const diff = getDifferentKeys(branding ?? {}, formBranding ?? {});
    console.log(branding);
    console.log(formBranding);
    const available = !!formBranding && !isLoading && diff.length > 0;
    if (available) {
      addNotification({
        id: "branding-saving-reminder",
        mode: "info",
        payload: "Don't forget to save your changes 🤓",
        dismissable: true,
      });
    } else {
      removeNotification("branding-saving-reminder");
    }

    return available;
  }, [addNotification, branding, formBranding, isLoading, removeNotification]);

  const routeInfo = usePageInfo();

  const formURL = useMemo(() => {
    const accountId = account?.id;
    if (!accountId) return undefined;
    return getWidgetUrl(accountId);
  }, [account]);

  const handleFormLink = () => {
    window.open(formURL, "_blank");
  };

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={routeInfo.name!}
      Icon={routeInfo.icon!}
      headerChildren={
        <Row>
          <MyButton
            variant="outline"
            Icon={FiExternalLink}
            label="Visit"
            onClick={handleFormLink}
            disabled={!formURL}
            containerStyle={{
              height: 32,
              borderRadius: "4px",
              minWidth: "none",
              px: 32,
              mr: 16,
            }}
            iconSize={18}
            labelStyle={{ fontSize: "16px" }}
          />
          <BrandingControl
            disabled={!saveAvailable}
            onSave={handleSave}
            onReset={handleReset}
          />
        </Row>
      }
    >
      {account && (
        <Div
          display="grid"
          gridTemplateAreas={
            "'name image border colors' 'logo image font colors' 'preview preview preview preview'"
          }
          gridGap="8px"
          gridTemplateColumns="300px 300px auto auto"
        >
          <Card name="logo" header="Logo">
            <UploadMedia
              mediaType="logo"
              accountId={account?.id}
              onUpload={refresh}
              value={formBranding?.logoUrl}
            />
            {/* <ImageUpload
            initialImageUrl={formBranding?.logoUrl}
            onCompleted={(url) =>
              setFormBranding((b) => ({
                ...b,
                logoUrl: convertImagePathToCDN(url),
              }))
            }
            width={56}
            height={56}
          /> */}
          </Card>
          <Card name="image" header="Banner">
            <UploadMedia
              mediaType="banner"
              accountId={account?.id}
              onUpload={refresh}
              value={formBranding?.imageUrl}
            />
          </Card>
          <Card name="font" header="Font">
            <Div width="100%">
              <FontSelector
                font={formBranding?.fontBody}
                onSelect={(fontBody) =>
                  setFormBranding((b) => ({ ...b, fontBody }))
                }
              />
            </Div>
          </Card>
          <Card name="name" header="Name">
            <Input
              label="Name"
              type="text"
              value={formBranding?.displayName}
              onChange={(displayName) =>
                setFormBranding((b) => ({ ...b, displayName }))
              }
              containerStyle={{ marginBottom: "16px" }}
            />
          </Card>
          <Card name="border" header="Border radius">
            <Input
              label="Radius"
              type="number"
              min={0}
              max={50}
              value={formBranding?.borderRadius}
              onChange={(borderRadius) =>
                setFormBranding((b) => ({
                  ...b,
                  borderRadius: parseInt(borderRadius, 10),
                }))
              }
              containerStyle={{ marginBottom: "16px" }}
            />
          </Card>
          <Card name="colors" header="Colors">
            <Col alignItems="stretch">
              <ColorPicker
                label="Accent background"
                color={formBranding?.colorBgPrimary!}
                onSelect={(colorBgPrimary) =>
                  setFormBranding((b) => ({ ...b, colorBgPrimary }))
                }
              />
              <ColorPicker
                label="Accent text"
                color={formBranding?.colorTextPrimary!}
                onSelect={(colorTextPrimary) =>
                  setFormBranding((b) => ({ ...b, colorTextPrimary }))
                }
              />
              <ColorPicker
                label="Form background"
                color={formBranding?.colorBgForm!}
                onSelect={(colorBgForm) =>
                  setFormBranding((b) => ({ ...b, colorBgForm }))
                }
              />
              <ColorPicker
                label="Form text"
                color={formBranding?.colorTextHigh!}
                onSelect={(colorTextHigh) =>
                  setFormBranding((b) => ({ ...b, colorTextHigh }))
                }
              />
            </Col>
          </Card>
        </Div>
      )}
    </Page>
  );
}
